



































































































import { computed, defineComponent, PropType, ref, watch } from "@vue/composition-api";
import { debounce } from "lodash-es";
import { ContentFile } from "@scrile/api-provider/dist/api/ContentFilesProvider";
import { ContentSet, InputContentSetUpdate } from "@scrile/api-provider/dist/api/ContentSetProvider";
import { ContentFeedTypeEnum } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import { EventPost } from "@scrile/api-provider/dist/projects/scrileConnect/EventPostProvider";
import { getElement } from "@/lib/elementHelpers";
import { $t } from "@/lib/i18nHelpers";
import config from "@/config";
import { PostFileData } from "@/types";
import BaseSpinner from "@/components/BaseSpinner";
import BaseStepper from "@/components/BaseStepper";
import ContentFeedPreview from "@/components/ContentFeedPreview";
import ModalView from "@/components/ModalView";
import useStepperData from "@/components/PostCreate/hooks/useStepperData";
import EventDetails from "./components/EventDetails";
import AllowCommentsToggle from "../../components/AllowCommentsToggle";
import InputWithMentions from "../../components/InputWithMetions";
import PostDraggableView from "../../components/PostDraggableView";
import PostFilesArea from "../../components/PostFilesArea";
import PostHashtagsSettings from "../../components/PostHashtagsSettings";
import SafeForWorkToggle from "../../components/SafeForWorkToggle";
import SchedulePostSettings from "../../components/SchedulePostSettings";
import StepperButtons from "../../components/StepperButtons";
import { FileUploadData } from "../../types";

export default defineComponent({
  name: "EventCreateView",
  components: {
    AllowCommentsToggle,
    StepperButtons,
    SchedulePostSettings,
    PostHashtagsSettings,
    ContentFeedPreview,
    EventDetails,
    BaseStepper,
    PostFilesArea,
    InputWithMentions,
    SafeForWorkToggle,
    PostDraggableView,
    BaseSpinner,
    ModalView,
  },
  props: {
    show: { type: Boolean, default: false },
    loading: { type: Boolean, required: true },
    saving: { type: Boolean, default: false },
    postData: { type: Object as PropType<ContentSet | null> },
    fileList: { type: Array as PropType<PostFileData[]>, required: true },
    previewFiles: { type: Array as PropType<Array<ContentFile | FileUploadData>>, required: true },
    loadingUploadUrls: { type: Boolean, default: false },
    isDraft: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const stepperEl = ref();
    const showReorder = ref(false);
    const eventData = ref<EventPost | null>(null);
    const description = ref(props.postData?.description ?? "");
    const hashtagsAvailable = computed(() => config.featureHashtags);
    const allowManageReviews = computed(() => config.featureManageFeedReviews);
    const allowManageSchedule = computed(() => props.isDraft || !props.postData?.publishTime);
    const showSecondStep = computed(
      () => allowManageSchedule.value || hashtagsAvailable.value || allowManageReviews.value
    );
    const stepsArray = computed(() => {
      return ["filesAndDetails", ...(showSecondStep.value ? ["settings"] : []), "preview"];
    });

    const { currentStep, steps, getStepName } = useStepperData(stepsArray);

    const title = computed(() => (props.isDraft ? $t("New event") : $t("Edit event")));

    const priceData = {
      subscriptionLevelId: "0",
      pricePackageId: "0",
    };

    const onCurrentStepChange = () => {
      getElement<HTMLElement>(stepperEl.value).scrollIntoView({
        block: "start",
        behavior: "auto",
      });
    };

    const onReorderFiles = (ids: string[]) => {
      emit("draggable:update-list", ids);
      showReorder.value = false;
    };

    const onPublish = () => {
      const data: Omit<InputContentSetUpdate, "id"> = {
        description: description.value,
        published: true,
        allowReviews: allowManageReviews.value ? props.postData?.allowReviews : undefined,
        tagIds: props.postData?.tagIds ?? [],
      };
      emit("update", { data, closeOnSave: true });
    };

    watch(
      description,
      debounce((description: string) => {
        const data: Omit<InputContentSetUpdate, "id"> = {
          description,
        };
        emit("partialUpdate", { data });
      }, 500)
    );

    return {
      stepperEl,
      title,
      showReorder,
      description,
      steps,
      hashtagsAvailable,
      allowManageReviews,
      allowManageSchedule,
      showSecondStep,
      ContentFeedTypeEnum,
      priceData,
      eventData,
      currentStep,
      onCurrentStepChange,
      onReorderFiles,
      getStepName,
      onPublish,
    };
  },
});
